/**
 * Voice Recorder Component
 * Handles audio recording and transcription
 */
import React, { useState } from 'react';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import APIService from '../services/APIService';

const VoiceRecorder = ({ onTranscriptionReady, onCancel }) => {
  const { isRecording, transcript, setTranscript, error, setError, startRecording, stopRecording } =
    useAudioRecorder();
  const [transcribing, setTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const handleStartRecording = async () => {
    setTranscript('');
    setError(null);
    await startRecording();
  };

  const handleStopAndTranscribe = async () => {
    try {
      setTranscribing(true);

      // Get audio base64
      const audioBase64 = await stopRecording();

      // Send to backend for transcription
      const result = await APIService.transcribeAudio(audioBase64);
      setTranscript(result.transcription);
      setTranscribing(false);
    } catch (err) {
      setError(`Transcription error: ${err.message}`);
      setTranscribing(false);
    }
  };

  const handleUseTranscription = () => {
    if (transcript.trim()) {
      onTranscriptionReady(transcript);
    } else {
      setError('Transcription is empty');
    }
  };

  if (!isRecording && !transcript && !transcribing) {
    return (
      <div className="voice-recorder">
        <div className="recorder-container">
          <h3>🎤 Voice Input</h3>
          <p>Record your answer and we'll transcribe it for you</p>

          {error && <div className="error-message">{error}</div>}

          <button onClick={handleStartRecording} className="primary-button">
            🎙️ Start Recording
          </button>

          <button onClick={onCancel} className="secondary-button">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="voice-recorder">
        <div className="recorder-container">
          <h3>🎤 Recording...</h3>
          <div className="recording-indicator">
            <div className="recording-dot"></div>
            <span className="recording-time">{recordingTime}s</span>
          </div>

          <button onClick={handleStopAndTranscribe} className="danger-button">
            ⏹️ Stop Recording
          </button>
        </div>
      </div>
    );
  }

  if (transcribing) {
    return (
      <div className="voice-recorder">
        <div className="recorder-container">
          <h3>⏳ Transcribing...</h3>
          <p>Please wait while we convert your audio to text</p>
        </div>
      </div>
    );
  }

  if (transcript) {
    return (
      <div className="voice-recorder">
        <div className="recorder-container">
          <h3>✅ Transcription Complete</h3>

          {error && <div className="error-message">{error}</div>}

          <div className="transcription-display">
            <h4>Your answer:</h4>
            <p className="transcription-text">{transcript}</p>
          </div>

          <div className="button-group">
            <button onClick={handleStartRecording} className="secondary-button">
              🔄 Re-record
            </button>
            <button onClick={handleUseTranscription} className="primary-button">
              ✓ Use This Answer
            </button>
          </div>

          <button onClick={onCancel} className="text-button">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default VoiceRecorder;
